import React, { useState, useEffect, useRef } from "react";
import Icon from "../common/Icon";
import { BellRing, AddUser, RingIcon } from "../../assets/images/image";
import FollowersModal from "../modals/FollowersModal";
import NotificationsModal from "../modals/NotificationsModal";
import PreviewModal from "../../admin/src/components/Modals/PreviewModal";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";



const TopBar = ({ rightContent, onCreatePost, onMenuToggle }) => {
  const [showFollowers, setShowFollowers] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState({ community: [], faithstream: [] });
  const [selectedItem, setSelectedItem] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const searchRef = useRef(null);
  const queryClient = useQueryClient();
  const [debouncedQuery, setDebouncedQuery] = useState("");
  // Read the existing query data
  const { data: followersData } = useQuery({
    queryKey: ["pending-requests", 1],
    queryFn: () => api.fetchPendingRequests(1,10),
    keepPreviousData: true,
  });
  // Safely get count
  const followerCount = followersData?.pagination?.total || 0;

  const { data: NotificationData } = useQuery({
    queryKey: ["user-notifications", 1],
    queryFn: () => api.fetchNotifications(1),

    keepPreviousData: true,
  });
  const unReadNotificationCount = NotificationData?.unread_count || 0;


  // ── Debounce: wait 350ms after user stops typing before hitting API ──────
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── TanStack Query — same pattern as Categories.jsx ──────────────────────
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["global-search", debouncedQuery],
    queryFn: () => api.globalSearchCommunityPostStreams(debouncedQuery),
    enabled: debouncedQuery.length >= 2,   // only search when 2+ chars
    keepPreviousData: true,
  });

  // ── Map API response → UI shape ──────────────────────────────────────────
  // const BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
  const API_ROOT = (
    process.env.REACT_APP_API_URL || "http://localhost:5000/api"
  ).replace("/api", "");

  const resolveUrl = (path) => {
    if (!path) return null;
    return path.startsWith("http") ? path : `${API_ROOT}${path}`;
  };

  // posts → community items
  const communityResults = (data?.posts || []).map((post) => ({
    id: post.id,
    type: "community",
    title: (
      post.content?.trim() ||
      `${post.before_text || ""} ${post.after_text || ""}`.trim()
    ).slice(0, 40) + (
        (
          post.content?.trim() ||
          `${post.before_text || ""} ${post.after_text || ""}`.trim()
        ).length > 40
          ? "…"
          : ""
      ),
    author: post.author_name,
    time: new Date(post.created_at).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric"
    }),
    before_text: post.before_text,
    after_text: post.after_text,
    before_attachment: post.before_media,
    after_attachment: post.after_media,
    content: post.content,
    post_type: post.type,
    likes: post.bless_count,
    comments: post.comment_count,
    attachment: resolveUrl(post.attachment),
    avatarColor: "#1F3A60",
    authorAvatar: resolveUrl(post.author_avatar),
    category: post.type === "normal" ? "Community" : post.type,
  }));

  // media_items → faithstream items
  const faithstreamResults = (data?.media_items || []).map((item) => ({
    id: item.id,
    type: "faithstream",
    title: item.title,
    author: item.author,
    time: new Date(item.created_at).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric"
    }),
    media_url: resolveUrl(item.media_url),
    thumbnail: resolveUrl(item.thumbnail),
    has_video: Boolean(item.has_video) || item.media_url?.endsWith(".mp4"),
    views: 0,
    duration: "",
    category: item.category_name,
  }));

  const totalResults = communityResults.length + faithstreamResults.length;
  const isSearching = (isLoading || isFetching) && debouncedQuery.length >= 2;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const q = e.target.value;
    setSearchQuery(q);
    setShowSearchResults(true);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setDebouncedQuery("");
    setShowSearchResults(false);
  };

  const handleResultClick = (item) => {
    console.log({ item });
    setSelectedItem(item);
    setShowSearchResults(false);
    setSearchQuery("");
    setDebouncedQuery("");
    if (item.type === "community") setShowPreviewModal(true);
    if (item.type === "faithstream") setShowMediaModal(true);
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close on ESC
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") clearSearch();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Whether to show the dropdown at all
  const showDropdown =
    showSearchResults && searchQuery.trim().length >= 2;
  return (
    <>
      <div className="flex items-center justify-between px-3 sm:px-4 md:px-6 py-4 sm:py-5 bg-white border-b border-gray-100 sticky top-0 z-20 gap-2 sm:gap-3 flex-shrink-0 relative">

        <button
          onClick={onMenuToggle}
          className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-gray-100 flex-shrink-0"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#374151"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {/* Search area */}
        <div
          className="flex-1 min-w-0 max-w-[756px] px-2 sm:px-6 lg:px-14 relative"
          ref={searchRef}
        >
          {/* Input */}
          <div className="flex items-center gap-2 bg-[#F9FAFB] rounded-[10px] px-3 sm:px-4 py-2 sm:py-3 transition-colors focus-within:bg-white focus-within:border focus-within:border-[#1F3A60] focus-within:shadow-sm">
            {isSearching ? (
              <svg className="animate-spin flex-shrink-0" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            ) : (
              <Icon name="search" size={18} className="text-gray-400 flex-shrink-0" />
            )}
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => { if (searchQuery.trim().length >= 2) setShowSearchResults(true); }}
              placeholder="Search for community, streams..."
              className="flex-1 min-w-0 bg-transparent outline-none text-[12px] sm:text-[14px] text-gray-700 placeholder:text-[12px] sm:placeholder:text-[14px] placeholder-gray-400 font-body"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
              >
                <Icon name="x" size={16} className="text-gray-400" />
              </button>
            )}
          </div>

          {/* Dropdown */}
          {showDropdown && (
            <>
              {/* Mobile backdrop */}
              <div
                className="fixed inset-0 z-40 sm:hidden"
                onClick={() => setShowSearchResults(false)}
              />

              <div
                className="
                  fixed top-[72px] left-0 right-0 z-50
                  sm:absolute sm:top-full sm:left-0 sm:right-0 sm:z-40
                  mx-0 sm:mx-6 lg:mx-14
                  sm:mt-2
                  bg-white rounded-xl sm:rounded-xl
                  border-t sm:border border-[#E8EDF3]
                  overflow-hidden
                  max-h-[calc(100vh-400px)] sm:max-h-[500px]
                  overflow-y-auto
                  [scrollbar-width:none] [-ms-overflow-style:none]
                  [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:h-0
                "
                style={{
                  width: 'calc(100% - 24px)',
                  marginLeft: '12px',
                  marginRight: '12px',
                }}
              >
                {/* Loading skeleton */}
                {isSearching ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                        <div className="w-12 h-12 rounded-lg bg-gray-200 flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-gray-200 rounded w-3/4" />
                          <div className="h-2 bg-gray-100 rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : totalResults === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Icon name="search" size={28} className="text-gray-300" />
                    </div>
                    <p className="text-gray-400 text-sm font-medium">No results found</p>
                    <p className="text-gray-300 text-xs mt-1">Try different search terms</p>
                  </div>
                ) : (
                  <div className="p-3">
                    {/* Results count */}
                    {/* <div className="px-3 py-2 mb-2">
                      <p className="text-sm text-black">
                        Found {totalResults} result{totalResults !== 1 ? "s" : ""}
                      </p>
                    </div> */}

                    {/* ── Community Posts ── */}
                    {communityResults.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 px-3 py-1.5 mb-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#1F3A60]" />
                          <span className="text-xs font-semibold text-[#1F3A60] uppercase tracking-wider">
                            Community Posts
                             {/* ({communityResults.length}) */}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {communityResults.slice(0, 5).map((item) => (
                            <div
                              key={item.id}
                              onClick={() => handleResultClick(item)}
                              className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F8FAFC] transition-all cursor-pointer"
                            >
                              {/* Avatar or image */}
                              {/* <div className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden bg-gray-100">
                                {item.image ? (
                                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-[#1F3A60]/10 flex items-center justify-center">
                                    <Icon name="file-text" size={20} className="text-[#1F3A60]/40" />
                                  </div>
                                )}
                              </div> */}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#0F1D2E] truncate">{item.title}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-gray-500">{item.author}</span>
                                  <span className="w-1 h-1 bg-gray-300 rounded-full" />
                                  <span className="text-xs text-gray-400">{item.time}</span>
                                </div>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-[10px] px-2 py-0.5 bg-[#1F3A60]/10 text-[#1F3A60] rounded-full">
                                    {item.category}
                                  </span>
                                  <span className="text-[10px] text-gray-400">🤲 {item.likes}</span>
                                  <span className="text-[10px] text-gray-400">💬 {item.comments}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* {communityResults.length > 5 && (
                            <div className="px-3 py-2 text-center">
                              <span className="text-xs text-gray-400">
                                +{communityResults.length - 5} more posts
                              </span>
                            </div>
                          )} */}
                        </div>
                      </div>
                    )}

                    {/* ── Faith Stream ── */}
                    {faithstreamResults.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 px-3 py-1.5 mb-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#1F3A60]" />
                          <span className="text-xs font-semibold text-[#1F3A60] uppercase tracking-wider">
                            Faith Stream 
                            {/* ({faithstreamResults.length}) */}
                          </span>
                        </div>
                        <div className="space-y-1">
                          {faithstreamResults.slice(0, 5).map((item) => (
                            <div
                              key={item.id}
                              onClick={() => handleResultClick(item)}
                              className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F8FAFC] transition-all cursor-pointer"
                            >
                              <div className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden bg-gray-100 relative">
                                {item.thumbnail ? (
                                  <img src={item.thumbnail} alt={item.title} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                    <Icon name="play" size={20} className="text-gray-400" />
                                  </div>
                                )}
                                {item.has_video && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-6 h-6 bg-black/50 rounded-full flex items-center justify-center">
                                      <Icon name="play" size={12} className="text-white" />
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#0F1D2E] truncate">{item.title}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-gray-500">{item.author}</span>
                                  <span className="w-1 h-1 bg-gray-300 rounded-full" />
                                  <span className="text-xs text-gray-400">{item.time}</span>
                                </div>
                                {/* <div className="flex items-center gap-3 mt-1">
                                  <span className="text-[10px] px-2 py-0.5 bg-red-50 text-red-600 rounded-full">
                                    {item.category || "Video"}
                                  </span>
                                  {item.duration && (
                                    <span className="text-[10px] text-gray-400">⏱️ {item.duration}</span>
                                  )}
                                </div> */}
                              </div>
                            </div>
                          ))}
                          {/* {faithstreamResults.length > 5 && (
                            <div className="px-3 py-2 text-center">
                              <span className="text-xs text-gray-400">
                                +{faithstreamResults.length - 5} more videos
                              </span>
                            </div>
                          )} */}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {rightContent || (
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <button
              onClick={() => setShowFollowers(true)}
              className="bg-[#1F3147] flex justify-center items-center w-[38px] h-[38px] sm:w-[43px] sm:h-[43px] rounded-[30.99px] hover:bg-[#2a405c] transition-colors relative"
            >
              <img src={AddUser} alt="Followers" />

              {/* Count Badge */}
              {followerCount > 0 && (
                <div className="absolute -top-1 -right-1 bg-[#FF3B5C] text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 shadow">
                  {followerCount > 9 ? "9+" : followerCount}
                </div>
              )}
            </button>
            <button
              onClick={() => setShowNotifications(true)}
              className="relative bg-[#1F3147] flex justify-center items-center w-[38px] h-[38px] sm:w-[43px] sm:h-[43px] rounded-[30.99px] hover:bg-[#2a405c] transition-colors"
            >
              <img src={RingIcon} alt="Notifications" />

              {unReadNotificationCount > 0 && (
                <div className="absolute -top-1 -right-1 bg-[#FF3B5C] text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 shadow-md">
                  {unReadNotificationCount > 9 ? "9+" : unReadNotificationCount}
                </div>
              )}
            </button>

            <button
              onClick={onCreatePost}
              className="flex items-center gap-2 px-3 sm:px-6 lg:px-10 py-2 sm:py-3
              bg-[#FEDC5A] text-[#000000] rounded-[10px]
              text-[12px] sm:text-[14px] lg:text-[17px] transition-all
              font-body whitespace-nowrap"
            >
              <Icon name="edit" size={16} className="hidden sm:block" />
              Post
            </button>
          </div>
        )}
      </div>

      <FollowersModal
        isOpen={showFollowers}
        onClose={() => setShowFollowers(false)}
      />

      <NotificationsModal
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

      {/* Community post preview */}
      {showPreviewModal && selectedItem && (
        <PreviewModal
          item={{
            authorAvatar: selectedItem.authorAvatar,
            name: selectedItem.author,
            time: selectedItem.time,
            avatarColor: selectedItem.avatarColor || "#1F3A60",
            content: selectedItem.content,
            likes: selectedItem.likes,
            comments: selectedItem.comments,
            image: selectedItem.image,
            title: selectedItem.title,
            category: selectedItem.category,
            id: selectedItem.id,
            attachment: selectedItem.attachment,
            before_text: selectedItem.before_text,
            after_text: selectedItem.after_text,
            before_attachment: selectedItem.before_attachment,
            after_attachment: selectedItem.after_attachment,
            post_type: selectedItem.post_type


          }}
          onClose={() => { setShowPreviewModal(false); setSelectedItem(null); }}
        />
      )}

      {/* Faith stream video modal */}
      {showMediaModal && selectedItem && (
        <div
          onClick={() => { setShowMediaModal(false); setSelectedItem(null); }}
          className="fixed inset-0 bg-black/80 z-[999] flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-[24px] w-full max-w-[720px] max-h-[90vh] overflow-y-auto shadow-2xl"
          >
            <div className="relative">
              <button
                onClick={() => { setShowMediaModal(false); setSelectedItem(null); }}
                className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
              >
                <Icon name="x" size={20} />
              </button>
              <video
                src={selectedItem.media_url}
                controls
                autoPlay
                className="w-full bg-black rounded-t-[24px]"
                style={{ maxHeight: "70vh" }}
              />
            </div>
            <div className="p-6">
              <h3 className="font-bold text-xl text-[#0F1D2E]">{selectedItem.title}</h3>
              <div className="flex items-center gap-3 mt-2">
                <p className="text-sm text-gray-500">{selectedItem.author}</p>
                {selectedItem.category && (
                  <>
                    <span className="w-1 h-1 bg-gray-300 rounded-full" />
                    <span className="text-xs px-2 py-0.5 bg-red-50 text-red-600 rounded-full">
                      {selectedItem.category}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TopBar;